import { useEffect, useState } from "react";
import {
  Radio, Plus, RefreshCw, Save, Trash2, Loader2, Volume2, Pause,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Brand } from "../../context/CompanyContext";

const VOICE_HINTS = {
  shimmer: "Brilhante, alegre — feminina (recomendada)",
  nova: "Energética, dinâmica — feminina",
  coral: "Quente, próxima — feminina",
  alloy: "Neutra, equilibrada",
  echo: "Suave, calma",
  fable: "Expressiva, narrativa",
  sage: "Sábia, ponderada",
  ash: "Clara, articulada",
  onyx: "Profunda, autoritária — masculina",
};

const SPEED_HINTS = "0.5 (devagar) a 1.5 (rápido). 0.95 soa natural para anúncios PT-PT.";

/**
 * Editor de slogans do Rádio LuxStay. Cada slogan é uma linha em
 * `db.marketing_slogans`. O admin pode editar texto/voz/velocidade,
 * pré-ouvir o resultado, e regenerar tudo após várias edições.
 */
export default function AdminRadio() {
  const [items, setItems] = useState([]);
  const [voices, setVoices] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [editing, setEditing] = useState({}); // slug → patch dict
  const [creating, setCreating] = useState(null); // {slug,text,voice,...} or null

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/marketing/slogans");
      setItems(data.items || []);
      setVoices(data.voices || []);
      setModels(data.models || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function setField(slug, key, value) {
    setEditing(prev => ({
      ...prev,
      [slug]: { ...(prev[slug] || {}), [key]: value },
    }));
  }
  function getField(s, key) {
    const patch = editing[s.slug] || {};
    return patch[key] !== undefined ? patch[key] : s[key];
  }
  const isDirty = (slug) => Object.keys(editing[slug] || {}).length > 0;

  async function save(slug) {
    const patch = editing[slug];
    if (!patch || Object.keys(patch).length === 0) return;
    setBusy(slug);
    try {
      const { data } = await api.patch(`/admin/marketing/slogans/${slug}`, patch);
      setItems(prev => prev.map(s => s.slug === slug ? data.slogan : s));
      setEditing(prev => { const c = { ...prev }; delete c[slug]; return c; });
      toast.success("Guardado.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  }

  async function preview(slug) {
    setBusy(slug);
    setPreviewUrl(null);
    try {
      const { data } = await api.post(`/admin/marketing/slogans/${slug}/preview`);
      const url = data.url; // /api/marketing/jingle/<hash>.mp3
      // Force a cache-buster on every preview click so the admin always
      // hears the latest TTS even if browser cached the previous version
      setPreviewUrl(`${url}?t=${Date.now()}`);
      toast.success("Áudio gerado. A reproduzir…");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  }

  async function remove(slug) {
    if (!window.confirm(`Eliminar o slogan "${slug}"? Esta ação não pode ser revertida.`)) return;
    setBusy(slug);
    try {
      await api.delete(`/admin/marketing/slogans/${slug}`);
      setItems(prev => prev.filter(s => s.slug !== slug));
      toast.success("Eliminado.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  }

  async function createNew() {
    if (!creating) return;
    setBusy("__new__");
    try {
      const { data } = await api.post("/admin/marketing/slogans", creating);
      setItems(prev => [...prev, data.slogan]);
      setCreating(null);
      toast.success("Slogan criado.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  }

  async function regenAll() {
    if (!window.confirm("Regenerar áudio de todos os slogans ativos? Pode demorar 30-60s.")) return;
    setBusy("__all__");
    try {
      const { data } = await api.post("/admin/marketing/regenerate");
      toast.success(`Regenerados ${data.regenerated.length} slogans.`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  }

  return (
    <div className="space-y-6" data-testid="admin-radio">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow text-[#d4af37] flex items-center gap-2">
            <Radio size={14} /> Admin · Marketing
          </div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Rádio <Brand short /></h1>
          <p className="text-sm text-[#a0b2af] mt-3 max-w-2xl">
            Anúncios em PT-PT que tocam para visitantes e clientes em rotação
            (a cada 60s) sobre a música de fundo. Edite o texto, escolha a voz
            e ouça antes de publicar — o cliente recebe a nova versão no
            próximo carregamento.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setCreating({
                    slug: "", text: "", voice: "shimmer",
                    speed: 0.95, model: "tts-1-hd", active: true, order: items.length,
                  })}
                  className="btn-gold text-xs uppercase tracking-widest px-4 py-2 inline-flex items-center gap-2"
                  data-testid="radio-add">
            <Plus size={12} /> Novo
          </button>
          <button onClick={regenAll} disabled={busy === "__all__"}
                  className="text-xs text-[#a0b2af] border border-[#1a4640] hover:border-[#d4af37] hover:text-[#d4af37] px-4 py-2 uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="radio-regen-all">
            {busy === "__all__" ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Regenerar tudo
          </button>
        </div>
      </header>

      {previewUrl && (
        <div className="lux-card p-4 flex items-center gap-3" data-testid="radio-preview">
          <Volume2 size={16} className="text-[#d4af37]" />
          <div className="text-xs text-[#a0b2af] flex-1">A reproduzir pré-visualização</div>
          <audio src={previewUrl} autoPlay controls className="h-9" />
          <button onClick={() => setPreviewUrl(null)}
                  className="text-[#a0b2af] hover:text-[#d4af37]"
                  aria-label="Fechar">
            <Pause size={14} />
          </button>
        </div>
      )}

      {creating && (
        <SloganForm
          mode="create"
          value={creating}
          onChange={setCreating}
          voices={voices}
          models={models}
          onSubmit={createNew}
          onCancel={() => setCreating(null)}
          busy={busy === "__new__"}
        />
      )}

      {loading ? (
        <div className="text-sm text-[#a0b2af]">A carregar…</div>
      ) : items.length === 0 ? (
        <div className="lux-card p-10 text-center text-sm text-[#a0b2af]">
          Sem slogans. Clique em <b>Novo</b> para criar o primeiro.
        </div>
      ) : (
        <div className="space-y-3" data-testid="radio-list">
          {items.map(s => (
            <article key={s.slug}
                     className={`lux-card p-5 space-y-3 ${!s.active ? "opacity-60" : ""}`}
                     data-testid={`radio-card-${s.slug}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-mono text-[11px] text-[#d4af37]">{s.slug}</div>
                  <div className="text-[10px] text-[#a0b2af] mt-0.5">
                    Posição: {s.order} · {s.active ? "Ativo" : "Inativo"} · Atualizado {new Date(s.updated_at).toLocaleString("pt-PT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => preview(s.slug)} disabled={busy === s.slug}
                          className="text-xs text-[#d4af37] border border-[#d4af37]/50 px-3 py-1.5 uppercase tracking-widest hover:bg-[#d4af37]/10 disabled:opacity-50 inline-flex items-center gap-1.5"
                          data-testid={`radio-preview-${s.slug}`}>
                    {busy === s.slug ? <Loader2 size={11} className="animate-spin" /> : <Volume2 size={11} />}
                    Ouvir
                  </button>
                  <button onClick={() => save(s.slug)} disabled={!isDirty(s.slug) || busy === s.slug}
                          className="btn-gold text-xs uppercase tracking-widest px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-30"
                          data-testid={`radio-save-${s.slug}`}>
                    {busy === s.slug ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                    Guardar
                  </button>
                  <button onClick={() => remove(s.slug)} disabled={busy === s.slug}
                          className="text-xs text-[#fca5a5] border border-[#ef4444]/40 hover:bg-[#ef4444]/10 px-3 py-1.5 uppercase tracking-widest disabled:opacity-50 inline-flex items-center gap-1.5"
                          data-testid={`radio-delete-${s.slug}`}>
                    <Trash2 size={11} /> Eliminar
                  </button>
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Texto (PT-PT)</label>
                <textarea
                  value={getField(s, "text")}
                  onChange={(e) => setField(s.slug, "text", e.target.value)}
                  rows={3}
                  maxLength={600}
                  className="w-full bg-[#062c27] border border-[#1a4640] px-3 py-2.5 text-sm focus:border-[#d4af37] focus:outline-none resize-none mt-1"
                  data-testid={`radio-text-${s.slug}`}
                />
                <div className="text-[10px] text-[#a0b2af] text-right">
                  {(getField(s, "text") || "").length} / 600
                </div>
              </div>

              <div className="grid md:grid-cols-4 gap-3">
                <Field label="Voz" testId={`radio-voice-${s.slug}`}>
                  <select value={getField(s, "voice")}
                          onChange={(e) => setField(s.slug, "voice", e.target.value)}
                          className="w-full bg-[#062c27] border border-[#1a4640] px-3 py-2 text-sm focus:border-[#d4af37] focus:outline-none">
                    {voices.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                  <div className="text-[10px] text-[#a0b2af] mt-1 italic">
                    {VOICE_HINTS[getField(s, "voice")] || ""}
                  </div>
                </Field>
                <Field label="Velocidade">
                  <input type="number" step="0.05" min="0.5" max="1.5"
                         value={getField(s, "speed")}
                         onChange={(e) => setField(s.slug, "speed", parseFloat(e.target.value))}
                         className="w-full bg-[#062c27] border border-[#1a4640] px-3 py-2 text-sm focus:border-[#d4af37] focus:outline-none" />
                  <div className="text-[10px] text-[#a0b2af] mt-1 italic">{SPEED_HINTS}</div>
                </Field>
                <Field label="Modelo">
                  <select value={getField(s, "model")}
                          onChange={(e) => setField(s.slug, "model", e.target.value)}
                          className="w-full bg-[#062c27] border border-[#1a4640] px-3 py-2 text-sm focus:border-[#d4af37] focus:outline-none">
                    {models.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <div className="text-[10px] text-[#a0b2af] mt-1 italic">
                    tts-1-hd: melhor qualidade · tts-1: mais barato
                  </div>
                </Field>
                <Field label="Ativo / Posição">
                  <div className="flex items-center gap-2">
                    <label className="inline-flex items-center gap-1.5 text-xs text-[#fdfbf7]">
                      <input type="checkbox" checked={!!getField(s, "active")}
                             onChange={(e) => setField(s.slug, "active", e.target.checked)} />
                      No ar
                    </label>
                    <input type="number" min="0"
                           value={getField(s, "order")}
                           onChange={(e) => setField(s.slug, "order", parseInt(e.target.value, 10) || 0)}
                           className="flex-1 bg-[#062c27] border border-[#1a4640] px-3 py-2 text-sm focus:border-[#d4af37] focus:outline-none" />
                  </div>
                </Field>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <label className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function SloganForm({ mode, value, onChange, voices, models, onSubmit, onCancel, busy }) {
  const update = (k, v) => onChange({ ...value, [k]: v });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
          className="lux-card p-5 space-y-3 ring-1 ring-[#d4af37]/30"
          data-testid="radio-form">
      <div className="font-display text-xl text-[#d4af37]">
        {mode === "create" ? "Novo slogan" : "Editar slogan"}
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Identificador (slug)</label>
          <input value={value.slug}
                 onChange={(e) => update("slug", e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))}
                 placeholder="ex.: oferta-natal"
                 className="w-full bg-[#062c27] border border-[#1a4640] px-3 py-2 text-sm focus:border-[#d4af37] focus:outline-none mt-1"
                 required maxLength={40}
                 data-testid="radio-form-slug" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Voz</label>
          <select value={value.voice}
                  onChange={(e) => update("voice", e.target.value)}
                  className="w-full bg-[#062c27] border border-[#1a4640] px-3 py-2 text-sm focus:border-[#d4af37] focus:outline-none mt-1">
            {voices.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Texto</label>
        <textarea value={value.text}
                  onChange={(e) => update("text", e.target.value)}
                  rows={3} required minLength={10} maxLength={600}
                  placeholder="ex.: Esta semana, 10% de desconto na sua primeira limpeza…"
                  className="w-full bg-[#062c27] border border-[#1a4640] px-3 py-2 text-sm focus:border-[#d4af37] focus:outline-none mt-1 resize-none"
                  data-testid="radio-form-text" />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={busy}
                className="text-xs uppercase tracking-widest text-[#a0b2af] hover:text-[#d4af37] px-3 py-2">
          Cancelar
        </button>
        <button type="submit" disabled={busy}
                className="btn-gold text-xs uppercase tracking-widest px-4 py-2 inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="radio-form-submit">
          {busy && <Loader2 size={12} className="animate-spin" />}
          {mode === "create" ? "Criar" : "Guardar"}
        </button>
      </div>
    </form>
  );
}
